/**
 * `lib/canvasEditFlag` — WHICH DOCUMENT SURFACE mounts.
 *
 * THERE IS ONE SURFACE. Owner directive, 2026-07-28, verbatim: *"delete entire
 * html view and just leave the canvas paginated view … let's pivot and focus
 * ONLY on paginated view"*. The product renders and edits documents with the
 * Zig→WASM canvas, fragmented into sheets. Nothing else.
 *
 * WHAT THIS FILE USED TO BE, so the deletion is legible in `git log` rather than
 * only in the diff. It was a TRI-STATE resolver over three surfaces:
 *
 *   · `canvas-paginated`  — the canvas, fragmented into sheets.
 *   · `canvas-continuous` — the same wasm, the same `PaintSink` seam, the same
 *                           SoA wire, laid out as one unpaginated column.
 *   · `dom-continuous`    — the legacy `contenteditable` editor.
 *
 * …selected by the `<ResourceHeader>` segmented control, with a `?canvasEdit=`
 * (or `localStorage["ela.canvasEdit"]`) force for dev/E2E. `continuous` had just
 * been flipped from the DOM editor onto the canvas after web-page-layout
 * `3314543` landed the virtualised tile painter — measured over the real
 * `pdfz-layout.wasm` on an 89,412-word deliverable: a 476,880 CSS-px column
 * (29.1x the 32,767 px maximum safe canvas side) painted through 5 tiles whose
 * largest canvas was 1243x4093 device px, with typing and a Save round-trip to
 * Postgres. The pivot supersedes that: one fragmentation policy, not two.
 *
 * ── THE ACCESSIBILITY CONSEQUENCE, STATED PLAINLY ──────────────────────────
 *
 * The deleted `dom-continuous` editor was the accessibility fallback, and it was
 * a real one. Measured in Chrome on the same document, same session, before the
 * deletion: the DOM surface put 637,694 characters, 669 headings and 3,690
 * paragraphs in the accessibility tree. The canvas puts ZERO — every `<canvas>`
 * is `aria-hidden="true"` and the only accessible node is the offscreen
 * `<textarea aria-label="Document editor">` the input sink owns, whose value is
 * `""`. That measurement is identical for both canvas fragmentation policies.
 *
 * web-docz ships `DocumentMirror` (the accessible semantic projection of the
 * docz IR) and elaborate mounts it on the READ canvas
 * (`components/canvas/ReadCanvas.tsx`). NOTHING mounts a mirror on the EDIT
 * canvas. Until something does, the editing surface is not reachable by a screen
 * reader and there is no longer another surface to fall back to. This is a known,
 * accepted consequence of the pivot — not an oversight, and not fixed here.
 *
 * The command-level gaps (structural inserts, lists, tables, agent proposals,
 * block validation, partial block-kind conversion) are declared per verb in
 * `components/canvas/canvasCapabilities.ts`.
 */

/** Which document surface `<DoczCanvas>` mounts. One value — see the module doc. */
export type DocSurface = "canvas-paginated";

/**
 * THE surface decision, and it has one answer.
 *
 * Kept as a function rather than inlined as a constant so the decision has ONE
 * name in the codebase: if a second surface is ever reintroduced it is
 * reintroduced here, and every call site already asks the right question.
 */
export function resolveDocSurface(): DocSurface {
  return "canvas-paginated";
}
